import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { toast } from "react-toastify";

import { MapKind } from "../../hooks/useMakeMap";
import { Calculation, Field, Raster } from "../../models/mapLayer";
import {
  EditableLayer,
  Layer,
  PreservedUploadState,
  RasterWithFile
} from "../GeoMapLoaderModal.types";
import { processRasterFiles } from "./processRasterFiles";

interface UseEditModeHandlersProps {
  editableLayers: EditableLayer[];
  setEditableLayers: React.Dispatch<React.SetStateAction<EditableLayer[]>>;
  layers: Layer[];
  layerGroupName: string;
  autoCloseTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
  autoCloseDelayMs: number;
  handleClose: () => void;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  onUploadingChange: ((isUploading: boolean) => void) | undefined;
  onUploadProgress?: (progress: number) => void;
  /** Called if background upload fails, with preserved state for recovery */
  onUploadError?: (preservedState: PreservedUploadState) => void;
  setBackgroundUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  settings: { getAllSettings: () => Field[] } | undefined;
  // API functions
  uploadMap: (params: {
    id: number;
    file: File | null;
    name: string;
    expression: string;
    projFile?: File;
    unit: string;
    min?: number;
    max?: number;
    overrideFileName: string;
  }) => Promise<string>;
  deleteMapFromLayer: (params: { id: number; mapId: string }) => Promise<void>;
  updateMapLayer: (layer: EditableLayer) => Promise<void>;
  updateMap: (params: {
    id: number;
    map: { Raster?: Raster; Calculation?: Calculation };
  }) => Promise<void>;
  deleteMapLayer: (params: { id: number }) => Promise<void>;
  addMapLayer: (params: {
    name: string;
    projectId: string | null;
    group: string;
    metadata: null;
    maps: [];
  }) => Promise<number | undefined>;
  makeMap: (params: { id: number; kind: MapKind }) => Promise<unknown>;
  refetchGeoMapLayers: () => Promise<void>;
  // Shared helpers from create mode
  createCalculationMaps: (
    templateName: string
  ) => Array<{ Raster?: Raster; Calculation?: Calculation }>;
  processLayerMaps: (
    layer: Layer,
    layerId: number,
    progressBase?: number,
    progressSlice?: number
  ) => Promise<void>;
}

export function useEditModeHandlers({
  editableLayers,
  setEditableLayers,
  layers,
  layerGroupName,
  autoCloseTimerRef,
  autoCloseDelayMs,
  handleClose,
  setIsSaving,
  setIsUploading,
  onUploadingChange,
  onUploadProgress,
  onUploadError,
  setBackgroundUploadError,
  settings,
  uploadMap,
  deleteMapFromLayer,
  updateMapLayer,
  updateMap,
  deleteMapLayer,
  addMapLayer,
  makeMap,
  refetchGeoMapLayers,
  createCalculationMaps,
  processLayerMaps
}: UseEditModeHandlersProps) {
  // Use ref to ensure we always have the latest callback for async operations
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const onUploadProgressRef = useRef(onUploadProgress);
  onUploadProgressRef.current = onUploadProgress;

  // Ref for error callback to avoid stale closures in async operations
  const onUploadErrorRef = useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;

  // Helper function to update a specific map within an editable layer
  const updateEditableLayerMap = (
    layerId: number,
    map: { Raster?: Raster; Calculation?: Calculation },
    updater: (m: { Raster?: Raster; Calculation?: Calculation }) => {
      Raster?: Raster;
      Calculation?: Calculation;
    }
  ) => {
    setEditableLayers((prev) =>
      prev.map((layer) => {
        if (layer._id === layerId) {
          return {
            ...layer,
            maps: layer.maps.map((m) => {
              const isMatch =
                (map.Raster && m.Raster && m.Raster.id === map.Raster.id) ||
                (map.Calculation &&
                  m.Calculation &&
                  m.Calculation.id === map.Calculation.id);
              return isMatch ? updater(m) : m;
            })
          };
        }
        return layer;
      })
    );
  };

  // Handle file upload for existing layers in edit mode
  const handleEditLayerFilesSelected = async (layerId: number, files: File[]) => {
    setIsUploading(true);
    try {
      const newMaps = processRasterFiles(files, settings?.getAllSettings() ?? []);

      // Add new maps to the editable layer
      setEditableLayers((prev) =>
        prev.map((layer) =>
          layer._id === layerId ? { ...layer, maps: [...layer.maps, ...newMaps] } : layer
        )
      );
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to process files. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditLayerDeleteMap = async (
    layerId: number,
    map: { Raster?: Raster; Calculation?: Calculation }
  ) => {
    const mapId = map.Raster?.id ?? map.Calculation?.id;
    if (!mapId) return;

    try {
      // Call delete API immediately (same behavior as old GeoMap implementation)
      await deleteMapFromLayer({ id: layerId, mapId });

      // Update local state after successful delete
      setEditableLayers((prev) =>
        prev.map((layer) => {
          if (layer._id === layerId) {
            return {
              ...layer,
              maps: layer.maps.filter((m) => {
                if (map.Raster && m.Raster) {
                  return m.Raster.id !== map.Raster.id;
                }
                if (map.Calculation && m.Calculation) {
                  return m.Calculation.id !== map.Calculation.id;
                }
                return true;
              })
            };
          }
          return layer;
        })
      );

      // Refetch to update the cached data so reopening modal shows correct state
      await refetchGeoMapLayers();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Error deleting map. Please try again.");
    }
  };

  // Handler for updating layer name in local state (on each keystroke)
  const handleEditLayerNameChange = (layerId: number, name: string) => {
    setEditableLayers((prev) =>
      prev.map((layer) => {
        if (layer._id === layerId) {
          return { ...layer, name };
        }
        return layer;
      })
    );
  };

  // Handler to save layer name to API (on blur or Enter)
  const handleEditLayerNameSave = async (layerId: number) => {
    const layer = editableLayers.find((l) => l._id === layerId);
    if (!layer || layer.name.trim().length === 0) {
      return;
    }
    try {
      await updateMapLayer(layer);
      await refetchGeoMapLayers();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to update layer name");
    }
  };

  const handleEditLayerMapChange = async (
    layerId: number,
    map: { Raster?: Raster; Calculation?: Calculation },
    changes: { min?: number; max?: number; unit?: string }
  ) => {
    // Update local state
    const updatedMap = { ...map };
    if (updatedMap.Raster) {
      updatedMap.Raster = { ...updatedMap.Raster, ...changes };
    } else if (updatedMap.Calculation) {
      updatedMap.Calculation = { ...updatedMap.Calculation, ...changes };
    }

    updateEditableLayerMap(layerId, map, () => updatedMap);

    try {
      await updateMap({ id: layerId, map: updatedMap });
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to update map");
    }
  };

  const handleEditLayerMapNameChanged = async (
    layerId: number,
    map: { Raster?: Raster; Calculation?: Calculation },
    name: string
  ) => {
    // Update local state
    const updatedMap = { ...map };
    const target = updatedMap.Raster ?? updatedMap.Calculation;

    if (target) {
      // Update name
      target.name = name;

      // Look up field settings and auto-populate unit, min, max, and expression
      const foundField: Field = settings?.getAllSettings().find((f) => f.name === name);
      if (foundField) {
        target.unit = foundField.unit;
        target.min = foundField.min;
        target.max = foundField.max;
        if (foundField?.expression?.length > 0) {
          target.transformer = {
            type: "ExpressionTransform",
            expression: foundField.expression
          };
        }
      }
    }

    updateEditableLayerMap(layerId, map, () => updatedMap);

    try {
      await updateMap({ id: layerId, map: updatedMap });
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to update map name");
    }
  };

  const handleEditLayerExpressionValueChanged = async (
    layerId: number,
    map: { Raster?: Raster; Calculation?: Calculation },
    expression: string
  ) => {
    // Update local state
    const updatedMap = { ...map };
    if (updatedMap.Raster) {
      if (expression) {
        updatedMap.Raster = {
          ...updatedMap.Raster,
          transformer: { type: "ExpressionTransform" as const, expression }
        };
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { transformer, ...rest } = updatedMap.Raster;
        updatedMap.Raster = rest as Raster;
      }
    } else if (updatedMap.Calculation) {
      if (expression) {
        updatedMap.Calculation = {
          ...updatedMap.Calculation,
          transformer: { type: "ExpressionTransform" as const, expression }
        };
      }
    }

    updateEditableLayerMap(layerId, map, () => updatedMap);

    try {
      await updateMap({ id: layerId, map: updatedMap });
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to update expression");
    }
  };

  const handleEditLayerScaleValuesToggled = (
    layerId: number,
    map: { Raster?: Raster; Calculation?: Calculation },
    checked: boolean
  ) => {
    updateEditableLayerMap(layerId, map, (m) => {
      if (m.Raster) {
        if (checked) {
          const expression = m.Raster.transformer?.expression || "$";
          return {
            ...m,
            Raster: {
              ...m.Raster,
              transformer: { type: "ExpressionTransform" as const, expression }
            }
          };
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { transformer, ...rest } = m.Raster;
          return { ...m, Raster: rest as Raster };
        }
      }
      return m;
    });
  };

  // Add calculation map to an existing layer in edit mode
  // Uses optimistic UI: renders immediately, then persists in background
  const handleEditLayerAddCalculationMap = async (layerId: number) => {
    const layer = editableLayers.find((l) => l._id === layerId);
    if (!layer) return;

    const templateName = layer._selectedCalculationMapTemplate || "Single";
    const templateMaps = createCalculationMaps(templateName);

    // Filter out any maps that already exist in the layer (by name)
    const existingNames = new Set(layer.maps.map((m) => m.Calculation?.name));
    const newMapsToAdd = templateMaps.filter(
      (m) => m.Calculation && !existingNames.has(m.Calculation.name)
    );

    if (newMapsToAdd.length === 0) {
      toast.info("All maps from this template already exist in the layer");
      return;
    }

    // Create optimistic maps with temporary IDs for immediate UI update
    const optimisticMaps = newMapsToAdd.map((map, index) => ({
      Calculation: {
        ...map.Calculation,
        id: `temp-${Date.now()}-${index}`, // Temporary ID
        createdDate: {
          $date: new Date().toISOString()
        },
        createdBy: ""
      }
    }));

    // Immediately update UI with optimistic maps
    setEditableLayers((prev) =>
      prev.map((l) =>
        l._id === layerId ? { ...l, maps: [...l.maps, ...optimisticMaps] } : l
      )
    );

    // Persist to backend in background
    (async () => {
      try {
        const persistedMaps: Array<{
          tempId: string;
          realId: string;
          map: { Calculation: Calculation };
        }> = [];

        for (let i = 0; i < newMapsToAdd.length; i++) {
          const map = newMapsToAdd[i];
          if (!map.Calculation) continue;

          const tempId = optimisticMaps[i].Calculation.id;

          // Upload each calculation map to the backend
          const realId = await uploadMap({
            id: layerId,
            file: null,
            name: map.Calculation.name,
            expression: map.Calculation.transformer?.expression ?? "$",
            unit: map.Calculation.unit ?? "",
            min: map.Calculation.min,
            max: map.Calculation.max,
            overrideFileName: ""
          });

          persistedMaps.push({
            tempId,
            realId,
            map: {
              Calculation: {
                ...map.Calculation,
                id: realId,
                createdDate: {
                  $date: new Date().toISOString()
                },
                createdBy: ""
              }
            }
          });
        }

        // Update local state: replace temp IDs with real IDs
        setEditableLayers((prev) =>
          prev.map((l) => {
            if (l._id !== layerId) return l;
            return {
              ...l,
              maps: l.maps.map((m) => {
                if (!m.Calculation) return m;
                const persisted = persistedMaps.find(
                  (p) => p.tempId === m.Calculation.id
                );
                return persisted ? persisted.map : m;
              })
            };
          })
        );

        // Refetch to ensure cache is in sync
        await refetchGeoMapLayers();
      } catch (error) {
        Sentry.captureException(error);
        toast.error("Failed to save calculation maps. Please try again.");

        // Remove optimistic maps on error
        setEditableLayers((prev) =>
          prev.map((l) => {
            if (l._id !== layerId) return l;
            return {
              ...l,
              maps: l.maps.filter(
                (m) => !m.Calculation?.id?.toString().startsWith("temp-")
              )
            };
          })
        );
      }
    })();
  };

  // Delete an existing layer (already on server) - calls API then removes from local state
  const handleDeleteExistingLayer = async (layerId: number) => {
    // Check if this is the last existing layer in the group
    const isLastExistingLayer = editableLayers.length === 1;

    try {
      await deleteMapLayer({ id: layerId });
      await refetchGeoMapLayers();
      toast.success("Layer deleted successfully");

      // If this was the last existing layer, the backend deletes the entire group
      // So we should close the modal and return to the IPDB modal
      if (isLastExistingLayer) {
        handleClose();
        return;
      }

      setEditableLayers((prev) => prev.filter((layer) => layer._id !== layerId));
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete layer. Please try again.");
    }
  };

  // Handle update for edit mode
  const handleUpdate = async () => {
    // Check if there are any layers to process (existing or new)
    const newLayersWithMaps = layers.filter((layer) => layer.maps.length > 0);
    if (editableLayers.length === 0 && newLayersWithMaps.length === 0) {
      toast.error("No layers to update");
      return;
    }

    setIsSaving(true);
    onUploadingChangeRef.current?.(true);

    // Capture current state before closing modal (prevents stale state in async operations)
    const currentEditableLayers = editableLayers.map((layer) => ({
      ...layer,
      maps: [...layer.maps]
    }));
    const currentNewLayers = layers
      .filter((layer) => layer.maps.length > 0)
      .map((layer) => ({ ...layer, maps: [...layer.maps] }));
    const currentLayerGroupName = layerGroupName;

    // Preserve state for potential recovery if upload fails
    const errorMessage = "Update failed. Please review your changes and try again.";
    const preservedState: PreservedUploadState = {
      layers: currentNewLayers,
      editableLayers: currentEditableLayers,
      layerGroupName: currentLayerGroupName,
      selectedCalculationMapTemplate: null,
      isEditMode: true,
      errorMessage
    };

    // Close modal after delay
    autoCloseTimerRef.current = setTimeout(() => {
      handleClose();
    }, autoCloseDelayMs);

    // Perform updates in background
    (async () => {
      try {
        // Calculate total layers for progress allocation
        const totalLayers =
          currentEditableLayers.filter((l) => l._id).length + currentNewLayers.length;
        const slicePerLayer = totalLayers > 0 ? 80 / totalLayers : 0;
        let layerIndex = 0;

        onUploadProgressRef.current?.(10);

        // Update each existing layer (use captured state to avoid stale closures)
        for (const layer of currentEditableLayers) {
          const layerId = layer._id;
          if (!layerId) continue;

          // Separate maps into existing (to update) and new (to upload)
          const existingMaps: Array<{ Raster?: Raster; Calculation?: Calculation }> = [];
          const newRasterMaps: Array<{ Raster: RasterWithFile }> = [];

          for (const map of layer.maps) {
            // Check if this is a new raster map (has _file property)
            if (map.Raster) {
              const raster = map.Raster as RasterWithFile;
              if (raster._file) {
                // New raster map - needs to be uploaded
                newRasterMaps.push({ Raster: raster });
              } else {
                // Existing raster map - needs to be updated
                existingMaps.push(map as { Raster?: Raster; Calculation?: Calculation });
              }
            } else if (map.Calculation) {
              // Calculation maps are always existing (new ones are already uploaded immediately)
              existingMaps.push(map as { Raster?: Raster; Calculation?: Calculation });
            }
          }

          // Update existing maps
          for (const map of existingMaps) {
            await updateMap({
              id: layerId,
              map
            });
          }

          // Upload new raster maps
          for (const map of newRasterMaps) {
            const raster = map.Raster;
            // We already check for _file existence when categorizing maps,
            // but TypeScript needs this guard
            if (!raster._file) continue;

            await uploadMap({
              id: layerId,
              file: raster._file,
              name: raster.name,
              expression: raster.transformer?.expression ?? "",
              projFile: raster._projFile,
              unit: raster.unit ?? "",
              min: raster.min,
              max: raster.max,
              overrideFileName: raster._overrideFileName ?? ""
            });
          }

          // Rebuild the maps for this layer
          const layerBase = 10 + layerIndex * slicePerLayer;
          const makeMapPoint = layerBase + slicePerLayer * 0.75;
          const makeLsdPoint = layerBase + slicePerLayer;

          await makeMap({ id: layerId, kind: MapKind.Native });
          onUploadProgressRef.current?.(Math.round(makeMapPoint));

          await makeMap({ id: layerId, kind: MapKind.LSD });
          onUploadProgressRef.current?.(Math.round(makeLsdPoint));

          layerIndex++;
        }

        // Update group name on all existing layers if it was changed
        for (const layer of currentEditableLayers) {
          if (layer.group !== currentLayerGroupName) {
            await updateMapLayer({ ...layer, group: currentLayerGroupName });
          }
        }

        // Create new layers (reuse same flow as handleOk)
        for (const layer of currentNewLayers) {
          const layerId = await addMapLayer({
            name: layer.name,
            projectId: null,
            group: currentLayerGroupName,
            metadata: null,
            maps: []
          });

          if (!layerId) {
            throw new Error("Failed to get layer ID from response");
          }

          const layerBase = 10 + layerIndex * slicePerLayer;
          await processLayerMaps(layer, layerId, layerBase, slicePerLayer);
          layerIndex++;
        }

        // Reset uploading state BEFORE refetching so treeData rebuilds with correct state
        onUploadingChangeRef.current?.(false);

        await refetchGeoMapLayers();
        toast.success(
          currentNewLayers.length > 0
            ? "Layer maps updated and new layers created successfully!"
            : "Layer maps updated successfully!"
        );
      } catch (error) {
        Sentry.captureException(error);

        // Clear the auto-close timer so it doesn't close the modal after we reopen it
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = null;
        }

        // Set error and call error callback with preserved state for recovery
        setBackgroundUploadError(errorMessage);
        if (onUploadErrorRef.current) {
          onUploadErrorRef.current(preservedState);
        }
      } finally {
        onUploadingChangeRef.current?.(false);
      }
    })();
  };

  return {
    handleEditLayerFilesSelected,
    handleEditLayerDeleteMap,
    handleEditLayerNameChange,
    handleEditLayerNameSave,
    handleEditLayerMapChange,
    handleEditLayerMapNameChanged,
    handleEditLayerExpressionValueChanged,
    handleEditLayerScaleValuesToggled,
    handleEditLayerAddCalculationMap,
    handleDeleteExistingLayer,
    handleUpdate
  };
}
